import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '../auth/authApi';
import { useSessionStore, isTokenExpired } from '../../stores/sessionStore';

export function useAuthBootstrap() {
  const session = useSessionStore((state) => state.session);
  const setBootstrap = useSessionStore((state) => state.setBootstrap);
  const setSession = useSessionStore((state) => state.setSession);
  const clear = useSessionStore((state) => state.clear);

  const token = session?.access_token;
  const isExpired = isTokenExpired(token);

  const query = useQuery({
    queryKey: ['auth-bootstrap', token],
    enabled: Boolean(token && !isExpired),
    queryFn: async ({ signal }) => {
      // Add a 15-second client-side timeout as a fallback
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT: Bootstrap pendente por mais de 15s')), 15000)
      );

      const fetchPromise = (async () => {
        // authApi.me validates the token. We do NOT swallow 401/auth errors.
        const meResult = await authApi.me(token!, { signal });
        const rawUser = ((meResult as any)?.user || meResult || {}) as Record<string, any>;
        if (!rawUser?.id && !rawUser?.username && !rawUser?.email) {
          throw new Error('401: Usuário inválido ou não autenticado');
        }

        const effectiveTenantId = rawUser.tenant_id || 'vanguarda';
        const [bootstrap, tenantInfo] = await Promise.all([
          authApi.bootstrap(token!, { signal }).catch(() => null),
          authApi.tenantInfo(window.location.hostname, effectiveTenantId).catch(() => null),
        ]);

        const userObj = {
          ...rawUser,
          plan: tenantInfo?.plan || rawUser?.plan || 'growth',
          tenant_name: tenantInfo?.name || rawUser?.tenant_name || rawUser?.tenant_id,
          enabled_modules: tenantInfo?.enabled_modules ?? rawUser?.enabled_modules ?? null,
        };
        setSession({ ...session!, user: userObj });
        setBootstrap({ ...(bootstrap || {}), user: userObj, tenant: tenantInfo || undefined });
        return { me: meResult, bootstrap, tenantInfo };
      })();

      return Promise.race([fetchPromise, timeoutPromise]) as Promise<{ me: any; bootstrap: any; tenantInfo: any }>;
    },
    staleTime: 60_000,
    retry: (failureCount, error: any) => {
      const msg = error?.message || '';
      if (
        msg.includes('401') ||
        msg.toLowerCase().includes('unauthorized') ||
        msg.toLowerCase().includes('token') ||
        msg.toLowerCase().includes('sessão expirada')
      ) {
        return false;
      }
      return failureCount < 1;
    },
  });

  useEffect(() => {
    // If token is already expired client-side, clear immediately
    if (token && isExpired) {
      console.warn('[BOOTSTRAP] Token expirado detectado no cliente, limpando sessão');
      clear();
      return;
    }

    // Only clear session on 401 (Unauthorized) to avoid login loops on temporary server errors (500/502)
    if (query.error) {
      const msg = query.error.message || '';
      const isUnauthorized =
        msg.includes('401') || 
        msg.toLowerCase().includes('unauthorized') ||
        msg.toLowerCase().includes('token') ||
        msg.toLowerCase().includes('usuário inválido') ||
        msg.toLowerCase().includes('sessão expirada');
      
      if (isUnauthorized) {
        console.warn('[BOOTSTRAP] Unauthorized or expired session, clearing session:', msg);
        clear();
      } else {
        console.error('[BOOTSTRAP] Server error, preserving session:', query.error.message);
      }
    }
  }, [query.error, token, isExpired, clear]);

  return query;
}
